import {qtyNum} from './format';
import {isExcluded, stammLagerOf} from './availability';

// Frachtrechner des Auftrags-Editors — 1:1 aus dem Original (`shipWeights`
// 2867, `hasSprayCans` 2886, `palletDefault` 2892, `shipDest` 2904,
// `zoneFor`/`freightWeightJS`/`lookupPriceJS`/`bigCitySurchargeJS`/
// `freightCostJS` 2911–2977, `unitBoxVol`/`palletEstimate` 2977–3001,
// `freightComparison` 2539–2567, `eur` 2567).
//
// Gerechnet wird bewusst im Browser, reaktiv bei jeder Änderung an Menge,
// Lagerwahl oder Palettenzahl — genau wie im Original. Der Server hat mit
// `FreightCalculator` dieselbe Rechnung als Prüfinstanz; die Oberfläche
// vergleicht beide Ergebnisse und meldet eine Abweichung, statt still eine
// von zwei Wahrheiten zu zeigen (ABGLEICH N30).
//
// Unterschied zum Original: alle Werte kommen als Argument herein
// (`rows`, `stock`, `fracht`, `zip`) statt aus globalen Variablen.

/** Betrag mit Euro-Zeichen; `null` wird zum Gedankenstrich. */
export function eur(v) {
    return v == null
        ? '—'
        : Number(v).toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2}) + ' €';
}

/**
 * Kein einzelner Molotow-Artikel ist größer als ein halber Kubikmeter (die
 * größten Teile sind Displays und Masken um ~0,01 m³). Rechnet ein Artikelmaß
 * dennoch mehr, ist im weclapp-Stammsatz fast sicher die Einheit falsch (cm
 * oder mm statt Meter) — genau so entstand einmal eine Schätzung über 136
 * Paletten. Solche Maße sind unbrauchbar und werden wie „ohne Maße" behandelt
 * (ABGLEICH J29/J30).
 */
export const MAX_PLAUSIBLE_UNIT_M3 = 0.5;

/**
 * Obergrenze der Anzahl je Fracht-Zeile. weclapp-Versandkosten kennen keine
 * Menge, deshalb wird eine Zeile serverseitig in N Einzelpositionen zerlegt.
 * Bei dreistelligen Zahlen (kaputte Schätzung) wäre der Beleg unbedienbar und
 * in weclapp nicht mehr auf einmal zu löschen. Muss mit `MAX_FREIGHT_POS` im
 * Server übereinstimmen.
 */
export const MAX_FREIGHT_POS = 15;

/**
 * Höchstzahl vorgemerkter Versandkosten-Zeilen. Der Server verarbeitet nur die
 * ersten 20 Zeilen (`DiffSaver::filterShippingCost`); weitere würden still
 * wegfallen und werden deshalb gar nicht erst vorgemerkt.
 */
export const MAX_SHIPCOST_LINES = 20;

/** Geografisches Europa = Land-/Straßenfracht. Alles andere = Übersee. */
export const EUROPE_LAND = new Set(['DE', 'AT', 'BE', 'NL', 'LU', 'FR', 'IT', 'ES', 'PT', 'DK', 'SE', 'FI',
    'PL', 'CZ', 'SK', 'HU', 'SI', 'HR', 'RO', 'BG', 'GR', 'EE', 'LV', 'LT', 'IE', 'CH', 'LI', 'NO', 'GB', 'IS',
    'MT', 'CY', 'RS', 'BA', 'MK', 'AL', 'ME', 'XK', 'MD', 'UA', 'BY', 'TR', 'MC', 'SM', 'AD', 'VA', 'GI', 'FO']);

/**
 * Stückvolumen aus den Artikelmaßen.
 *
 * @returns {?number} `null` = keine Maße hinterlegt, `NaN` = unplausibel groß,
 *          sonst m³ je Stück.
 */
export function unitBoxVol(d) {
    if (!d || !(d.l > 0 && d.w > 0 && d.h > 0)) {
        return null;
    }
    const v = d.l * d.w * d.h;
    return v > MAX_PLAUSIBLE_UNIT_M3 ? NaN : v;
}

/**
 * Gewicht und Volumen je Stammlager aus den Positionen. Das Gewicht kommt
 * zuverlässig aus weclapp, das Volumen ist nur grobe Information.
 *
 * Gegenüber dem Original zusätzlich: Positionen mit der Lagerwahl „Keine"
 * zählen nicht mit — sie werden nicht geliefert und dürfen die Fracht nicht
 * verteuern (Stufe 1, ABGLEICH H27–H36).
 */
export function shipWeights(rows, stock) {
    const w = {Lahr: 0, SNV: 0, Cosmos: 0, total: 0};
    let vol = 0;
    let missing = 0;
    let units = 0;
    const oversized = [];

    for (const r of (rows || [])) {
        if (r.del || !r.articleId || r._new || isExcluded(r)) {
            continue;
        }
        const q = qtyNum(r.quantity);
        const d = (stock.dims && stock.dims[String(r.articleId)]) || {};
        const g = stammLagerOf(r, stock);
        if (w[g] === undefined) {
            w[g] = 0;
        }
        const wt = (d.weight || 0) * q;
        w[g] += wt;
        w.total += wt;
        units += q;

        // Volumen nur plausibel einrechnen — ein Artikel mit falscher
        // Maßeinheit darf die Palettenzahl nicht sprengen.
        const v0 = unitBoxVol(d);
        if (v0 === null) {
            missing++;
        } else if (Number.isNaN(v0)) {
            missing++;
            oversized.push({articleNumber: r.articleNumber, title: r.title});
        } else {
            vol += v0 * q;
        }
    }

    return {...w, vol, missing, oversized, units};
}

/**
 * Enthält die Sendung Spraydosen (VOC-Wert am Artikel)? Im Panel
 * überschreibbar (ABGLEICH J28).
 *
 * @param {?boolean} override - `null` = automatisch bestimmen.
 */
export function hasSprayCans(rows, stock, override) {
    if (override !== null && override !== undefined) {
        return !!override;
    }
    return (rows || []).some(
        (r) => !r.del && !r._new && !isExcluded(r) && ((stock.voc && stock.voc[String(r.articleId)]) || 0) > 0,
    );
}

/**
 * Grober Paletten-Startwert aus Volumen bzw. Gewicht. Bewusst nur ein
 * Vorschlag — die echte Zahl bestätigt der Lager- oder Vertriebsmensch
 * (ABGLEICH O16).
 */
export function palletDefault(volM3, weightKg) {
    if (!(volM3 > 0) && !(weightKg > 0)) {
        return 0;
    }
    return Math.max(1, Math.ceil((volM3 || 0) / 0.75), Math.ceil((weightKg || 0) / 600));
}

/**
 * Versandziel-Art samt passendem Palettentyp und Versandkosten-Artikel
 * (ABGLEICH J31):
 * Inland (DE) → EU-Palette (`PAL-INL`), Ausland Europa → Einwegpalette
 * (gleiche Grundfläche wie EU, daher `palletKey: 'eu'`; `PAL-AUS`),
 * Übersee → Seepalette im Übersee-Maß (`palletKey: 'uebersee'`; `PAL-AUS`).
 */
export function shipDest(stock, order) {
    const cc = String((stock.shipTo && stock.shipTo.countryCode)
        || (order && order.recordAddress && order.recordAddress.countryCode) || 'DE').toUpperCase();

    if (cc === 'DE') {
        return {kind: 'Inland', label: 'EU-Palette', palletKey: 'eu', articleNr: 'PAL-INL', cc};
    }
    if (EUROPE_LAND.has(cc)) {
        return {kind: 'Ausland', label: 'Einwegpalette', palletKey: 'eu', articleNr: 'PAL-AUS', cc};
    }
    return {kind: 'Übersee', label: 'Seepalette (Übersee)', palletKey: 'uebersee', articleNr: 'PAL-AUS', cc};
}

/** Tarif-Zone: erste zwei Stellen der PLZ als Zahl; Irland dreistellig. */
export function zoneFor(iso, zip) {
    const z = String(zip || '').trim().toUpperCase().replace(/\s+/g, '');
    if (!z) {
        return null;
    }
    if (iso === 'IE') {
        return z.slice(0, 3);
    }
    const n = parseInt(z.slice(0, 2), 10);
    return isNaN(n) ? z.slice(0, 2) : String(n);
}

/**
 * Frachtgewicht = Ist-Gewicht + 25 kg je Palette, mit den Mindestgewichten
 * des Ziellands (ABGLEICH N12/N14/N15).
 */
export function freightWeightJS(carrier, iso, weight, pallets, params) {
    const base = weight + params.weightPerPallet * pallets;
    if (carrier === 'dhl') {
        return (pallets > 0 && weight < 330) ? 330 : base;
    }
    const mw = params.minWeight[carrier];
    if (pallets <= 0) {
        return base;
    }
    if (carrier === 'schenkerLahr' && (params.nordic || []).includes(iso) && base < mw.nordic) {
        return mw.nordic;
    }
    if (iso === 'DE' && base < mw.DE) {
        return mw.DE;
    }
    if (iso !== 'DE' && base < (mw.export || 0)) {
        return mw.export;
    }
    return base;
}

/**
 * Preis aus der Zonen-Tabelle: kleinste Gewichtsstufe, die das Frachtgewicht
 * noch abdeckt (Nachbau von Excel `MATCH(…;-1)`, ABGLEICH N16). Die
 * Gewichtsstufen stehen absteigend.
 */
export function lookupPriceJS(tab, zone, fw) {
    if (!tab || zone == null || !tab.zones[zone]) {
        return null;
    }
    const W = tab.weights;
    let idx = null;
    for (let i = 0; i < W.length; i++) {
        if (W[i] >= fw) {
            idx = i;
        } else {
            break;
        }
    }
    return idx === null ? null : tab.zones[zone][idx];
}

/** Großstadtzuschlag, wenn die PLZ mit einem Präfix der Liste beginnt. */
export function bigCitySurchargeJS(fracht, iso, zip) {
    const list = (fracht && fracht.bigCities) || [];
    if (!list.length) {
        return 0;
    }
    const z = String(zip || '').replace(/\s+/g, '');
    const hit = list.some((p) => z.startsWith(String(p)));
    const bc = fracht.params.bigCitySurcharge;
    return hit ? (bc[iso] != null ? bc[iso] : bc.default) : 0;
}

/**
 * Vollständige Frachtkosten für EINEN Carrier und Weg: Grundfrachtpreis plus
 * alle Zuschläge, additiv (ABGLEICH N17–N26).
 *
 * @returns {{total?: number, freight?: number, fw?: number, zone?: ?string, err?: string}}
 */
export function freightCostJS(carrier, weight, pallets, spray, fracht, zip) {
    if (!fracht || !fracht.supported) {
        return {err: 'kein Tarif fürs Zielland'};
    }
    const iso = fracht.iso;
    const P = fracht.params;
    const zone = zoneFor(iso, zip);
    const tab = fracht.carriers[carrier];
    if (!tab) {
        return {err: 'Carrier nicht hinterlegt'};
    }

    const fw = freightWeightJS(carrier, iso, weight, pallets, P);
    const freight = lookupPriceJS(tab, zone, fw);
    if (freight == null) {
        return {
            err: zone == null ? 'keine PLZ' : `PLZ-Zone ${zone} / ${Math.round(fw)} kg nicht im Tarif`,
            zone,
            fw,
        };
    }

    const floater = freight * P.floater[carrier];
    const m = P.maut[carrier];
    const maut = freight * (m[iso] != null ? m[iso] : (m.default || 0));
    const ps = P.palletSurcharge[carrier];
    const pal = (iso === 'DE' ? ps.DE : ps.export) * pallets;
    const handling = (P.handling[carrier] || {})[iso] || 0;
    const curr = iso === 'CH' ? freight * P.currencySurchargeCH : 0;
    const trade = (iso === 'GB' && carrier !== 'dhl')
        ? P.tradeSurchargeUKPer100 * (Math.ceil(fw / 100) * 100 / 100)
        : 0;
    const big = bigCitySurchargeJS(fracht, iso, zip);
    const spraySur = spray ? P.sprayCanSurcharge[carrier] : 0;
    const cert = (carrier === 'dhl' && iso !== 'DE') ? P.spediteurCertDHL : 0;

    const total = freight + floater + maut + pal + handling + curr + trade + big + spraySur + cert;

    return {total, freight, fw, zone, floater, maut, pal, handling, curr, trade, big, spraySur, cert};
}

/**
 * Frachtvergleich der drei Wege in echten Euro (ABGLEICH J25–J28):
 * „Alles ab Lahr", „Alles ab SNV" und „2 Direktlieferungen".
 *
 * Die Kennzeichnung „günstigste" wird NUR vergeben, wenn wirklich in beiden
 * Lägern Ware liegt — sonst gibt es keine echte Alternative, das andere Lager
 * müsste erst beistellen (ABGLEICH J26).
 *
 * @param {object[]} rows
 * @param {object} stock
 * @param {object} order
 * @param {{total: ?number, Lahr: ?number, SNV: ?number}} palletInput
 * @param {?boolean} sprayOverride
 */
export function freightComparison(rows, stock, order, palletInput, sprayOverride) {
    const fracht = stock.fracht;
    if (!fracht) {
        return null;
    }

    const sw = shipWeights(rows, stock);
    const spray = hasSprayCans(rows, stock, sprayOverride);
    const zip = (stock.shipTo && stock.shipTo.zip) || '';
    const mixed = sw.Lahr > 0 && sw.SNV > 0;

    const dTotal = palletDefault(sw.vol, sw.total);
    const dLahr = sw.Lahr > 0 ? Math.max(1, Math.ceil(sw.Lahr / 600)) : 0;
    const dSNV = sw.SNV > 0 ? Math.max(1, Math.ceil(sw.SNV / 600)) : 0;

    const pi = palletInput || {};
    const pTotal = pi.total != null ? pi.total : dTotal;
    const pLahr = pi.Lahr != null ? pi.Lahr : dLahr;
    const pSNV = pi.SNV != null ? pi.SNV : dSNV;

    const lahr = freightCostJS('schenkerLahr', sw.total, pTotal, spray, fracht, zip);
    const snv = freightCostJS('schenkerWeilheim', sw.total, pTotal, spray, fracht, zip);

    // Split (2 Direktlieferungen) nur rechnen, wenn wirklich in beiden Lägern
    // Ware liegt.
    const sl = mixed ? freightCostJS('schenkerLahr', sw.Lahr, pLahr, spray, fracht, zip) : {err: 'keine Lahr-Ware'};
    const ss = mixed ? freightCostJS('schenkerWeilheim', sw.SNV, pSNV, spray, fracht, zip) : {err: 'keine SNV-Ware'};
    const splitTotal = (mixed && sl.total != null && ss.total != null) ? sl.total + ss.total : null;

    let cheapest = null;
    if (mixed) {
        const opts = {lahr: lahr.total, snv: snv.total, split: splitTotal};
        let min = Infinity;
        for (const k of ['lahr', 'snv', 'split']) {
            if (opts[k] != null && opts[k] < min) {
                min = opts[k];
                cheapest = k;
            }
        }
    }

    return {
        sw,
        spray,
        mixed,
        pTotal,
        pLahr,
        pSNV,
        dTotal,
        dLahr,
        dSNV,
        lahr,
        snv,
        split: {l: sl, s: ss, total: splitTotal},
        supported: fracht.supported,
        cheapest,
        opts: {lahr: lahr.total, snv: snv.total, split: splitTotal},
    };
}

/**
 * Grobe Paletten-Schätzung aus den Artikelmaßen (ABGLEICH J32):
 * Gesamtvolumen geteilt durch Grundfläche × Nutzhöhe × Füllfaktor, je
 * Palettentyp. Artikel mit unplausiblen Maßen werden nicht eingerechnet und
 * in `oversized` gemeldet.
 */
export function palletEstimate(rows, stock) {
    const pallets = stock.pallets;
    if (!pallets || !pallets.types) {
        return null;
    }

    let vol = 0;
    let weight = 0;
    let missing = 0;
    const oversized = [];

    for (const r of (rows || [])) {
        if (r.del || !r.articleId || r._new || isExcluded(r)) {
            continue;
        }
        const q = qtyNum(r.quantity);
        const d = (stock.dims && stock.dims[String(r.articleId)]) || {};
        const v0 = unitBoxVol(d);
        if (v0 === null) {
            missing++;
            continue;
        }
        if (Number.isNaN(v0)) {
            missing++;
            oversized.push({articleNumber: r.articleNumber, title: r.title});
            continue;
        }
        vol += v0 * q;
        weight += (d.weight || 0) * q;
    }

    const H = pallets.heightM || 2;
    const F = pallets.fillFactor || 0.6;
    const types = pallets.types.map((t) => {
        const cap = t.l * t.w * H * F;
        return {key: t.key, label: t.label, n: cap > 0 ? Math.max(1, Math.ceil(vol / cap)) : 0};
    });

    return {vol, weight, missing, oversized, types};
}
